import { Flex, FormControl, FormErrorMessage } from "@chakra-ui/react";
import { FormProvider, useForm } from "react-hook-form";
import Input from "../inputs/Input";
import { forwardRef, useImperativeHandle } from "react";
import type { Material } from "../../types/material.types";
import FileImageInput from "../FileImageInput";
import { useFile } from "../../hooks/file.hooks";

type MaterialFormProps = {
	value?: Material;
};

export type MaterialFormHandler = {
	values: () => Partial<Material>;
	valid: () => Promise<boolean>;
};

const MaterialForm = forwardRef<MaterialFormHandler, MaterialFormProps>(
	({ value }, ref) => {
		const methods = useForm();
		const { data: file } = useFile({ id: value?.fileId });

		useImperativeHandle(ref, () => ({
			valid: () => methods.trigger(),
			values: () => {
				const values = methods.getValues();
				return {
					label: values.label,
					status: values.status,
					sageId: values.sageId ?? "",
					number: values.number,
					file: values.file,
				};
			},
		}));
		return (
			<FormProvider {...methods}>
				<form style={{ marginTop: "32px", marginBottom: "32px" }}>
					<Flex direction="column" gap="24px">
						<FormControl
							isInvalid={!!methods.formState.errors.file}
							className="flex justify-center"
						>
							<div className="flex flex-col items-center">
								<FileImageInput
									defaultValue={file}
									onFileChange={(files: File[] | null) => {
										if (!files || files.length === 0) {
											methods.setValue("file", null);
											return;
										}

										methods.setValue("file", files[0]);
									}}
									{...methods.register("file")}
								></FileImageInput>
								<FormErrorMessage>
									<>{methods.formState.errors?.file?.message}</>
								</FormErrorMessage>
							</div>
						</FormControl>
						<FormControl isInvalid={!!methods.formState.errors.label}>
							<Input
								id="label"
								placeholder="Label"
								defaultValue={value ? value.label : undefined}
								tabIndex={1}
								{...methods.register("label", {
									required: "Ce champ est obligatoire",
								})}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.label?.message}</>
							</FormErrorMessage>
						</FormControl>

						<Flex justify="between" gap={4}>
							<FormControl isInvalid={!!methods.formState.errors.sageId}>
								<Input
									id="sageId"
									placeholder="ID sage"
									tabIndex={2}
									defaultValue={value ? value.sageId : undefined}
									{...methods.register("sageId")}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.sageId?.message}</>
								</FormErrorMessage>
							</FormControl>

							<FormControl isInvalid={!!methods.formState.errors.number}>
								<Input
									id="number"
									placeholder="Numéro"
									tabIndex={3}
									defaultValue={value ? value.number : undefined}
									{...methods.register("number", {
										required: "Ce champ est obligatoire",
									})}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.number?.message}</>
								</FormErrorMessage>
							</FormControl>
						</Flex>
					</Flex>
				</form>
			</FormProvider>
		);
	},
);

export default MaterialForm;
